import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import { toast } from "react-toastify"; // 1. Import toast
import "react-toastify/dist/ReactToastify.css"; // (Ensure this is imported somewhere in your app)
import "./styles.css";
import { FaEdit, FaSave, FaTimes, FaCamera } from "react-icons/fa";
import logoImage from "../../assets/SignUp/logo.jpeg";

const FULL_TENANT_STRUCTURE = {
  name: "",
  companyName: "",
  contactPhone: "",
  email: "",
  address: "",
  location: "",
  taxId: "",
  registrationNumber: "",
  totalAcreage: 0,
  currency: "",
  isSubscribed: false,
  subscribedFrom: null,
  subscribedTo: null,
  isDeleted: false,
  companyLogo: logoImage,
};

const CompanyProfile = () => {
  const { t } = useTranslation();
  const { userData, token } = useContext(UserContext);

  const [profileData, setProfileData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  // 2. The 'error' state is no longer needed

  useEffect(() => {
    if (!userData?.Tenant?.id || !token) {
      setLoading(false);
      return;
    }
    const tenantId = userData.Tenant.id;

    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/tenant/${tenantId}`, {
          // 3. Authorization header is correct for your specific API
          headers: { Authorization: token },
        });

        const fetchedData = response.data.items[0] || {};
        const completeData = { ...FULL_TENANT_STRUCTURE, ...fetchedData };

        setProfileData(completeData);
        setOriginalData(completeData);
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
        // 4. Use toast for error feedback
        toast.error(t("Failed to load company profile."));
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userData, token, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setProfileData(originalData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const tenantId = userData?.Tenant?.id;
    if (!tenantId) {
      toast.error("Could not find tenant ID to update.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    const { newLogoFile, ...tenantDetails } = profileData;

    const tenantDetailsToUpdate = {
      name: tenantDetails.name,
      companyName: tenantDetails.companyName,
      contactPhone: tenantDetails.contactPhone,
      email: tenantDetails.email,
      address: tenantDetails.address,
      location: tenantDetails.location,
      taxId: tenantDetails.taxId,
      registrationNumber: tenantDetails.registrationNumber,
      totalAcreage: tenantDetails.totalAcreage,
      currency: tenantDetails.currency,
      isSubscribed: tenantDetails.isSubscribed,
      subscribedFrom: tenantDetails.subscribedFrom,
      subscribedTo: tenantDetails.subscribedTo,
      isDeleted: tenantDetails.isDeleted,
    };

    formData.append(
      "tenant",
      new Blob([JSON.stringify(tenantDetailsToUpdate)], {
        type: "application/json",
      })
    );

    if (newLogoFile) {
      formData.append("company_logo", newLogoFile);
    }

    try {
      await axios.put(
        `/api/tenant/tenantUpdateWithCompanyNameAndLogo/${tenantId}`,
        formData,
        { headers: { Authorization: token } }
      );
      setOriginalData(profileData);
      setIsEditing(false);
      // 5. Use toast for success feedback
      toast.success(t("Profile updated successfully!"));
    } catch (err) {
      console.error("Server response:", err.response);
      const errorMessage =
        err.response?.data?.error || t("Failed to save changes.");
      // 6. Use toast for error feedback
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileData({
        ...profileData,
        companyLogo: URL.createObjectURL(file),
        newLogoFile: file,
      });
    }
  };

  if (loading)
    return <div className="profile-message">{t("Loading Profile...")}</div>;
  // We removed the error display here as toasts will handle it.
  if (!profileData) return null;

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h2>{t("Company Profile")}</h2>
          {!isEditing ? (
            <button className="edit-button" onClick={handleEdit}>
              <FaEdit /> {t("Edit")}
            </button>
          ) : (
            <div className="edit-actions">
              <button className="cancel-button" onClick={handleCancel}>
                <FaTimes /> {t("Cancel")}
              </button>
              <button className="save-button" onClick={handleSave}>
                <FaSave /> {t("Save Changes")}
              </button>
            </div>
          )}
        </div>

        <div className="profile-body">
          <div className="profile-logo-section">
            <img
              src={profileData.companyLogo}
              alt="Company Logo"
              className="company-logo"
            />
            {isEditing && (
              <label htmlFor="logo-upload" className="logo-upload-button">
                <FaCamera /> {t("Change Logo")}
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </div>

          <div className="profile-details-grid">
            {/* Company Name */}
            <div className="profile-field">
              <label>{t("Company Name")}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="companyName"
                  value={profileData.companyName}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.companyName}</span>
              )}
            </div>

            {/* Contact Email */}
            <div className="profile-field">
              <label>{t("Contact Email")}</label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.email}</span>
              )}
            </div>

            {/* Contact Phone */}
            <div className="profile-field">
              <label>{t("Contact Phone")}</label>
              {isEditing ? (
                <input
                  type="tel"
                  name="contactPhone"
                  value={profileData.contactPhone}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.contactPhone}</span>
              )}
            </div>

            {/* Address */}
            <div className="profile-field">
              <label>{t("Address")}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="address"
                  value={profileData.address}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.address}</span>
              )}
            </div>

            {/* Tax ID */}
            <div className="profile-field">
              <label>{t("Tax ID")}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="taxId"
                  value={profileData.taxId}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.taxId}</span>
              )}
            </div>

            {/* Registration Number */}
            <div className="profile-field">
              <label>{t("Registration Number")}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="registrationNumber"
                  value={profileData.registrationNumber}
                  onChange={handleInputChange}
                />
              ) : (
                <span>{profileData.registrationNumber}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
